import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';
import '../styles/Pages.css';

function Settings() {
  const [preferences, setPreferences] = useState({
    leadScoreThreshold: '',
    statusChangeAlert: false,
    engagementScoreThreshold: '',
    customAlertMessage: '',
    emailNotifications: false,
  });
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'Loan Officer' });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchPreferencesAndUsers = async () => {
      try {
        const [preferencesResponse, usersResponse] = await Promise.all([
          axiosInstance.get('/alert-preferences'),
          axiosInstance.get('/users'),
        ]);
        setPreferences(preferencesResponse.data);
        setUsers(usersResponse.data || []);
        setError('');
      } catch (err) {
        setError('Failed to load data.');
      } finally {
        setLoading(false);
      }
    };

    fetchPreferencesAndUsers();
  }, []);

  const handlePreferencesChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPreferences((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleNewUserChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePreferencesSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    try {
      await axiosInstance.post('/alert-preferences', preferences);
      setSuccessMessage('Preferences updated successfully.');
    } catch (err) {
      setError('Failed to update preferences.');
    }
  };

  const handleNewUserSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    try {
      const response = await axiosInstance.post('/users', newUser);
      setUsers((prev) => [...prev, response.data]);
      setNewUser({ name: '', email: '', role: 'Loan Officer' });
      setSuccessMessage('New user added successfully.');
    } catch (err) {
      setError('Failed to add new user.');
    }
  };

  if (loading) return <div className="spinner">Loading...</div>;

  return (
    <div className="settings-container p-6 sm:p-8 bg-gradient-to-b from-gray-50 to-gray-100 min-h-screen">
      <h1 className="text-4xl font-extrabold text-center text-gray-800 mb-10">Settings</h1>

      {/* Alert Preferences */}
      <div className="card bg-white p-6 sm:p-8 shadow-xl rounded-2xl mb-10">
        <h2 className="text-2xl font-semibold text-gray-700 mb-6">Alert Preferences</h2>
        <form onSubmit={handlePreferencesSubmit} className="space-y-5">
          <div>
            <label htmlFor="leadScoreThreshold" className="block text-sm font-medium text-gray-600">
              Lead Score Threshold
            </label>
            <input
              type="number"
              id="leadScoreThreshold"
              name="leadScoreThreshold"
              value={preferences.leadScoreThreshold}
              onChange={handlePreferencesChange}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="statusChangeAlert"
              name="statusChangeAlert"
              checked={preferences.statusChangeAlert}
              onChange={handlePreferencesChange}
              className="form-checkbox h-5 w-5 text-indigo-600"
            />
            <label htmlFor="statusChangeAlert" className="text-sm font-medium text-gray-600">
              Enable Status Change Alert
            </label>
          </div>

          <div>
            <label htmlFor="engagementScoreThreshold" className="block text-sm font-medium text-gray-600">
              Engagement Score Threshold
            </label>
            <input
              type="number"
              id="engagementScoreThreshold"
              name="engagementScoreThreshold"
              value={preferences.engagementScoreThreshold}
              onChange={handlePreferencesChange}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="customAlertMessage" className="block text-sm font-medium text-gray-600">
              Custom Alert Message
            </label>
            <textarea
              id="customAlertMessage"
              name="customAlertMessage"
              value={preferences.customAlertMessage}
              onChange={handlePreferencesChange}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="emailNotifications"
              name="emailNotifications"
              checked={preferences.emailNotifications}
              onChange={handlePreferencesChange}
              className="form-checkbox h-5 w-5 text-indigo-600"
            />
            <label htmlFor="emailNotifications" className="text-sm font-medium text-gray-600">
              Enable Email Notifications
            </label>
          </div>

          <button type="submit" className="mt-4 w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg">
            Save Preferences
          </button>
        </form>
      </div>

      {/* New User */}
      <div className="card bg-white p-6 sm:p-8 shadow-xl rounded-2xl mb-10">
        <h2 className="text-2xl font-semibold text-gray-700 mb-6">Add New User</h2>
        <form onSubmit={handleNewUserSubmit} className="space-y-5">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-600">
              Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={newUser.name}
              onChange={handleNewUserChange}
              required
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-600">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={newUser.email}
              onChange={handleNewUserChange}
              required
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-600">
              Role
            </label>
            <select
              id="role"
              name="role"
              value={newUser.role}
              onChange={handleNewUserChange}
              className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option>Loan Officer</option>
              <option>Admin</option>
              <option>User</option>
            </select>
          </div>
          <button type="submit" className="mt-4 w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg">
            Add User
          </button>
        </form>
      </div>

      {/* Users List */}
      <div className="card bg-white p-6 sm:p-8 shadow-xl rounded-2xl">
        <h2 className="text-2xl font-semibold text-gray-700 mb-6">Manage Users</h2>
        {Array.isArray(users) && users.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {users.map((user) => (
              <li key={user.id} className="py-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-700 font-medium">
                    {user.name} — {user.role}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-gray-500">No users available.</p>
        )}
      </div>

      {successMessage && <div className="mt-6 text-green-600 text-center font-semibold">{successMessage}</div>}
      {error && <div className="mt-6 text-red-600 text-center font-semibold">{error}</div>}
    </div>
  );
}

export default Settings;